import React, { useCallback, useEffect, useRef, useState } from "react"
import { gen } from "./utils"
import splashImg from "../images/Sea_lion_Logo_04.png"
import { registerGateTrigger, unregisterGateTrigger } from "../utils/stagingGateSignal"

interface Props {
  children: React.ReactNode
}

// "revealing" = vault doors sliding open (children rendered behind them)
// "closing"   = vault doors sliding shut (wall form rendered behind them)
type GateState = "loading" | "revealing" | "open" | "wall" | "closing"

const MAX_RETRIES = 8
const RETRY_DELAY_MS = 1000
const VAULT_OPEN_MS = 1400   // how long the opening animation runs
const VAULT_CLOSE_MS = 1000  // closing is snappier

// CSS keyframes injected once at module level
const VAULT_KEYFRAMES = `
@keyframes _sgVaultOpenLeft {
  0%   { transform: translateX(0); }
  12%  { transform: translateX(3%); }
  100% { transform: translateX(-103%); }
}
@keyframes _sgVaultOpenRight {
  0%   { transform: translateX(0); }
  12%  { transform: translateX(-3%); }
  100% { transform: translateX(103%); }
}
@keyframes _sgVaultCloseLeft {
  0%   { transform: translateX(-103%); }
  80%  { transform: translateX(3%); }
  100% { transform: translateX(0); }
}
@keyframes _sgVaultCloseRight {
  0%   { transform: translateX(103%); }
  80%  { transform: translateX(-3%); }
  100% { transform: translateX(0); }
}
@keyframes _sgLockFadeOut {
  0%   { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
  70%  { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.6) rotate(-540deg); }
}
@keyframes _sgLockFadeIn {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  30%  { opacity: 1; transform: translate(-50%, -50%) scale(1.05); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(270deg); }
}
`

if (typeof document !== "undefined" && !document.getElementById("_sg-vault-styles")) {
  const styleEl = document.createElement("style")
  styleEl.id = "_sg-vault-styles"
  styleEl.textContent = VAULT_KEYFRAMES
  document.head.appendChild(styleEl)
}

// ── Vault door panels ─────────────────────────────────────────────────────────

function Rivet({ style }: { style: React.CSSProperties }) {
  return (
    <div style={{
      position: "absolute",
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: "radial-gradient(circle at 35% 35%, #6b7280, #374151)",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.6), 0 1px 1px rgba(255,255,255,0.08)",
      ...style,
    }} />
  )
}

function VaultDoor({ side, opening }: { side: "left" | "right"; opening: boolean }) {
  const isLeft = side === "left"
  const animDuration = opening ? VAULT_OPEN_MS : VAULT_CLOSE_MS
  const animName = opening
    ? (isLeft ? "_sgVaultOpenLeft" : "_sgVaultOpenRight")
    : (isLeft ? "_sgVaultCloseLeft" : "_sgVaultCloseRight")

  const panelStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    [isLeft ? "left" : "right"]: 0,
    width: "50%",
    height: "100%",
    zIndex: 10001,
    overflow: "hidden",
    background: [
      `linear-gradient(${isLeft ? "to right" : "to left"}, #0d0f14 0%, #1a1e2b 15%, #252b3a 40%, #1e2330 70%, #141720 100%)`,
      "repeating-linear-gradient(0deg, transparent, transparent 5px, rgba(255,255,255,0.012) 5px, rgba(255,255,255,0.012) 6px)",
      `linear-gradient(${isLeft ? "to right" : "to left"}, transparent 0%, rgba(255,255,255,0.04) 60%, transparent 100%)`,
    ].join(", "),
    boxShadow: isLeft
      ? "inset -8px 0 24px rgba(0,0,0,0.6), 4px 0 16px rgba(0,0,0,0.8)"
      : "inset 8px 0 24px rgba(0,0,0,0.6), -4px 0 16px rgba(0,0,0,0.8)",
    animation: `${animName} ${animDuration}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
  }

  const rivetPositions = [8, 18, 32, 50, 68, 82, 92] // % of height
  const outerRivetPositions = [15, 30, 50, 70, 85]

  return (
    <div style={panelStyle}>
      {/* Horizontal reinforcement bars */}
      {[15, 40, 60, 85].map((top) => (
        <div key={top} style={{
          position: "absolute",
          left: 0, right: 0,
          top: `${top}%`,
          height: 6,
          background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.3) 50%, rgba(255,255,255,0.03) 100%)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
        }} />
      ))}

      {/* Inner-edge rivets (along the seam) */}
      {rivetPositions.map((pct) => (
        <Rivet key={pct} style={{ [isLeft ? "right" : "left"]: "8px", top: `calc(${pct}% - 5px)` }} />
      ))}

      {/* Outer-edge rivets */}
      {outerRivetPositions.map((pct) => (
        <Rivet key={pct} style={{ [isLeft ? "left" : "right"]: "8px", top: `calc(${pct}% - 5px)` }} />
      ))}

      {/* Subtle sheen stripe */}
      <div style={{
        position: "absolute",
        top: 0, bottom: 0,
        [isLeft ? "right" : "left"]: "30%",
        width: "8%",
        background: `linear-gradient(${isLeft ? "to right" : "to left"}, transparent, rgba(255,255,255,0.025), transparent)`,
        pointerEvents: "none",
      }} />
    </div>
  )
}

function LockIndicator({ opening }: { opening: boolean }) {
  const animName = opening ? "_sgLockFadeOut" : "_sgLockFadeIn"
  const animDuration = opening ? VAULT_OPEN_MS : VAULT_CLOSE_MS

  return (
    <div style={{
      position: "fixed",
      top: "50%",
      left: "50%",
      zIndex: 10002,
      width: 56,
      height: 56,
      animation: `${animName} ${animDuration}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
      transformOrigin: "center center",
    }}>
      {/* Outer ring */}
      <div style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        border: "3px solid #4b5563",
        background: "radial-gradient(circle at 40% 35%, #374151, #111827)",
        boxShadow: "0 0 20px rgba(0,0,0,0.8), inset 0 1px 3px rgba(255,255,255,0.1)",
      }} />
      {/* Spokes */}
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <div key={deg} style={{
          position: "absolute",
          top: "50%", left: "50%",
          width: 2, height: 20,
          marginLeft: -1, marginTop: -20,
          background: "#6b7280",
          transformOrigin: "bottom center",
          transform: `rotate(${deg}deg)`,
          borderRadius: 1,
        }} />
      ))}
      {/* Center hub */}
      <div style={{
        position: "absolute",
        top: "50%", left: "50%",
        width: 12, height: 12,
        marginTop: -6, marginLeft: -6,
        borderRadius: "50%",
        background: opening ? "#10b981" : "#ef4444",
        boxShadow: `0 0 8px ${opening ? "rgba(16,185,129,0.7)" : "rgba(239,68,68,0.7)"}`,
        transition: "background 0.3s, box-shadow 0.3s",
      }} />
    </div>
  )
}

function VaultOverlay({ opening }: { opening: boolean }) {
  return (
    <>
      <VaultDoor side="left" opening={opening} />
      <VaultDoor side="right" opening={opening} />
      <LockIndicator opening={opening} />
    </>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function StagingGateWall({ children }: Props) {
  const [gateState, setGateState] = useState<GateState>("loading")
  const [error, setError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const checkStatus = useCallback(async (attempt = 0): Promise<void> => {
    try {
      const r = await fetch(gen("/api/staging-gate-status"), {
        credentials: "same-origin",
      })
      if (!r.ok) throw new Error("non-ok response")
      const data: { active: boolean; granted: boolean } = await r.json()
      if (!data.active) {
        // Gate is off — open immediately, no animation
        setGateState("open")
      } else if (data.granted) {
        // Gate is on and this session has valid access — play vault door opening
        setGateState("revealing")
      } else {
        // Gate is on, no access yet — show wall form directly
        setGateState("wall")
        if (new URLSearchParams(window.location.search).get("stagingError")) {
          setError(true)
        }
      }
    } catch {
      if (attempt < MAX_RETRIES) {
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS))
        return checkStatus(attempt + 1)
      }
      setGateState("open") // fail open after exhausting retries
    }
  }, [])

  // Initial check on mount
  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  // Advance vault animation states after their durations
  useEffect(() => {
    if (gateState === "revealing") {
      const t = setTimeout(() => setGateState("open"), VAULT_OPEN_MS)
      return () => clearTimeout(t)
    }
    if (gateState === "closing") {
      const t = setTimeout(() => setGateState("wall"), VAULT_CLOSE_MS)
      return () => clearTimeout(t)
    }
  }, [gateState])

  // Signal listener — closes the vault instead of an abrupt wall switch
  useEffect(() => {
    registerGateTrigger(() => setGateState("closing"))
    return () => unregisterGateTrigger()
  }, [])

  useEffect(() => {
    if (gateState === "wall") {
      inputRef.current?.focus()
    }
  }, [gateState])

  if (gateState === "loading") return null

  if (gateState === "open") return <>{children}</>

  const showWall = gateState === "wall" || gateState === "closing"
  const showChildren = gateState === "revealing"

  return (
    <>
      {/* Content rendered beneath the vault doors during the transition */}
      {showChildren && <>{children}</>}
      {showWall && <WallForm inputRef={inputRef} error={error} />}

      {/* Vault door overlay — only during transitions */}
      {(gateState === "revealing" || gateState === "closing") && (
        <VaultOverlay opening={gateState === "revealing"} />
      )}
    </>
  )
}

// ── Wall form (extracted so it can be rendered behind closing doors) ───────────

function WallForm({
  inputRef,
  error,
}: {
  inputRef: React.RefObject<HTMLInputElement>
  error: boolean
}) {
  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <span style={styles.badge}>Staging Environment</span>
        <div style={styles.splashWrap}>
          <img src={splashImg} alt="Swimmingly" style={styles.splashImg} />
        </div>
        <h1 style={styles.heading}>Internal Access Only</h1>
        <p style={styles.subtitle}>
          This is the <strong>Swimmingly testing environment</strong> and is
          intended for internal development and QA testing only.
        </p>
        <p style={styles.prodLink}>
          Looking for the live app?{" "}
          <a
            href="https://clubhouse.swimmingly.app"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            clubhouse.swimmingly.app
          </a>
        </p>

        <hr style={styles.divider} />

        {error && (
          <div style={styles.errorBox}>
            Incorrect access code. Please try again.
          </div>
        )}

        <form method="POST" action="/api/staging-gate" style={styles.form}>
          <label htmlFor="stagingAccessCode" style={styles.label}>
            Enter the staging access code to continue:
          </label>
          <input
            ref={inputRef}
            type="password"
            id="stagingAccessCode"
            name="accessCode"
            placeholder="Access code"
            autoComplete="off"
            required
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = "#101242")}
            onBlur={(e) => (e.target.style.borderColor = "#d9d9d9")}
          />
          <button type="submit" style={styles.button}>
            Continue to Staging
          </button>
        </form>

        <p style={styles.footer}>swimminglytesting.ninja</p>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "#f0f2f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 9999,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    padding: "40px 36px",
    width: "100%",
    maxWidth: 440,
    textAlign: "center",
  },
  badge: {
    display: "inline-block",
    background: "#fff7e6",
    color: "#d46b08",
    border: "1px solid #ffd591",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.5px",
    padding: "3px 10px",
    marginBottom: 20,
    textTransform: "uppercase",
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 14,
    color: "#595959",
    lineHeight: 1.6,
    marginBottom: 6,
  },
  prodLink: {
    fontSize: 13,
    color: "#8c8c8c",
    marginBottom: 28,
  },
  link: {
    color: "#101242",
    textDecoration: "none",
    fontWeight: 500,
  },
  divider: {
    border: "none",
    borderTop: "1px solid #f0f0f0",
    marginBottom: 24,
  },
  errorBox: {
    background: "#fff1f0",
    border: "1px solid #ffa39e",
    borderRadius: 8,
    color: "#cf1322",
    fontSize: 13,
    padding: "10px 14px",
    textAlign: "left",
    marginBottom: 12,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: "#262626",
    textAlign: "left",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
  },
  button: {
    width: "100%",
    padding: 10,
    background: "#101242",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  footer: {
    marginTop: 24,
    fontSize: 12,
    color: "#bfbfbf",
  },
  splashWrap: {
    display: "flex",
    justifyContent: "center",
    margin: "16px 0 20px",
  },
  splashImg: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    boxShadow: "0 4px 16px rgba(16, 18, 66, 0.18)",
    display: "block",
    objectFit: "cover",
  },
}
